/**
 * Substrate-v2 deterministic locator algorithm.
 *
 * `buildLocator(snapshot, ref, variableValues) -> SelectorStrategy` is a pure
 * function: it consumes a parsed substrate, an ephemeral ref, and the workflow
 * variable bag, and emits a {@link SelectorStrategy} ready for cache replay.
 * No live DOM access, no Effect, no I/O.
 *
 * See `docs/api/services/snapshot-substrate.md` for the design rationale and
 * predicate ladder.
 *
 * **Ancestor scoping (implementation note):** ancestor scoping is flattened to
 * a single identifiable anchor (via {@link isAncestorIdentifiable}) plus an
 * optional `parentChain` of intermediate ancestors, falling through to the
 * indexed-fallback when no anchor is identifiable.
 *
 * @module
 */

import type {
  ParentContext,
  ParentContextNode,
  ParentContextDynamicText,
  RoleSelector,
  SelectorStrategy,
  FrameContext,
} from '@chatsdet/types';
import {
  isVolatileIdentifier,
  stableIdentifierPrefix,
  stableAttrSelector,
} from '@chatsdet/types';
import type { SnapshotElement, SnapshotElementSource } from '@chatsdet/browser-core';

/**
 * A binding chosen during the templating pass. Surfaces every variable
 * substitution `buildLocator` made so callers can log decisions for review.
 */
export interface VariableBinding {
  /** Variable key (e.g. `customer_name`). */
  key: string;
  /** Literal value at recording time. */
  value: string;
  /** Substrate field where the match landed. */
  field: TemplatedField;
  /** Number of nodes whose value was rewritten with this binding. */
  occurrences: number;
}

export interface BuildLocatorResult {
  selectors: SelectorStrategy;
  /** Predicate name that produced the locator, for logging/metrics. */
  predicate: string;
  /** Variable bindings used while templating. */
  bindings: VariableBinding[];
  /**
   * True when no predicate produced a snapshot-unique match and we fell
   * through to the indexed fallback. Recording middleware uses this to decide
   * whether to enqueue a selector-agent task.
   */
  irreducible: boolean;
}

type TemplatedField = 'text' | 'aria-label' | 'placeholder' | 'value';

/** Field a predicate reads — used by the source-durability gate. */
type PredicateField = 'text' | 'aria-label' | 'placeholder' | 'src' | 'data-field';

const REPEATING_ANCESTOR_ROLES = new Set([
  'row',
  'listitem',
  'gridcell',
  'article',
  'option',
  'treeitem',
]);

interface NormalizedNode {
  el: SnapshotElement;
  /** Templated accessible name (`text`), with variable values replaced by `{{key}}`. */
  textTemplate?: string;
  /** Templated `aria-label` attribute. */
  ariaLabelTemplate?: string;
  /** Templated `placeholder` attribute. */
  placeholderTemplate?: string;
  /** Templated `value`/`inputValue`. */
  valueTemplate?: string;
  /** Bindings applied to this node (subset of the global bag). */
  bindings: VariableBinding[];
  /** Parent in the normalized tree, or null for roots. */
  parent: NormalizedNode | null;
  /** Refs of all descendants (including self). Built once during flatten. */
  descendantRefs: Set<string>;
}

/**
 * Build a {@link SelectorStrategy} for `ref` from the parsed substrate.
 * Returns the strategy plus diagnostics. Callers should pass the same
 * `variableValues` that recording will pass at replay.
 */
export function buildLocator(
  snapshot: SnapshotElement[],
  ref: string,
  variableValues: Record<string, string> = {}
): BuildLocatorResult {
  const { flat, byRef } = flatten(snapshot);
  const target = byRef.get(ref);
  if (!target) {
    return {
      selectors: {},
      predicate: 'none',
      bindings: [],
      irreducible: true,
    };
  }

  // Pass 1 — variable templating across the whole snapshot. Uniqueness
  // checks must scan templates so multiple rows that differ only in the
  // bound variable collapse into one match.
  const allBindings = templateAll(flat, variableValues);

  // Pass 2 — precompute uniqueness lookup tables for the identifiability
  // check. Built once; each ancestor probe is O(1).
  const identityIndex = buildIdentityIndex(flat);

  // Pass 3 — predicate ladder. First predicate that's globally unique
  // wins. If none unique, recurse under each ancestor closest-first
  // looking for predicate+scope that's unique within the subtree and
  // resolves back to the target.
  const frameContext = frameContextFor(target.el);
  const baseDynamicText = buildDynamicText(target);
  const ancestors = ancestorChain(target);

  // testId is special-cased: each testId is independent, so we pick the
  // first one that's globally unique rather than running the predicate
  // ladder on a set-overlap match.
  const uniqueTestId = pickUniqueTestId(target, flat);
  if (uniqueTestId) {
    return {
      selectors: applyContexts({ testId: uniqueTestId }, frameContext, baseDynamicText),
      predicate: 'testId',
      bindings: allBindings,
      irreducible: false,
    };
  }

  // iconClass is also special-cased: an icon-only control whose <svg> carries
  // a stable lucide class (e.g. `lucide-sliders-horizontal`) has no
  // accessible name in the substrate, so the role+name ladder cannot apply.
  // The DOM grounding is the icon class itself; emit a CSS `:has()` anchor
  // when it's globally unique for the target's role.
  const uniqueIconClass = pickUniqueIconClass(target, flat);
  if (uniqueIconClass) {
    const role: RoleSelector = { role: target.el.role };
    return {
      selectors: applyContexts(
        { role, css: roleScopedCss(target.el.role, `:has(svg.${uniqueIconClass})`) },
        frameContext,
        baseDynamicText
      ),
      predicate: 'iconClass',
      bindings: allBindings,
      irreducible: false,
    };
  }

  // semanticClass: a target whose own className carries a meaningful token
  // (e.g. `delete-btn`, `IconButton-filter`) that's globally unique for the
  // role. CSS class selectors are valid Playwright locators; the substrate
  // carries the raw class via `[~semantic-class=…]` and the algorithm
  // anchors on it before falling through to the role+name ladder.
  const uniqueSemanticClass = pickUniqueSemanticClass(target, flat);
  if (uniqueSemanticClass) {
    const role: RoleSelector = { role: target.el.role };
    return {
      selectors: applyContexts(
        { role, css: roleScopedCss(target.el.role, `.${uniqueSemanticClass}`) },
        frameContext,
        baseDynamicText
      ),
      predicate: 'semanticClass',
      bindings: allBindings,
      irreducible: false,
    };
  }

  const volatileTestIdText = buildVolatileTestIdTextSelector(target, flat);
  if (volatileTestIdText) {
    return {
      selectors: applyContexts(volatileTestIdText, frameContext, baseDynamicText),
      predicate: 'volatileTestId+containsText',
      bindings: allBindings,
      irreducible: false,
    };
  }

  for (const p of PREDICATES) {
    if (!p.applies(target)) continue;
    if (isPredicateBlockedBySource(p, target)) continue;

    // Global uniqueness — count and short-circuit at 2.
    const matches = uniquenessMatches(flat, target, p);
    if (matches === 1) {
      const sel = p.toStrategy(target, flat);
      return {
        selectors: applyContexts(sel, frameContext, baseDynamicText),
        predicate: p.name,
        bindings: allBindings,
        irreducible: false,
      };
    }

    const rowVariableScope = findRowVariableScope({
      flat,
      target,
      predicate: p,
      ancestors,
      variableValues,
    });
    if (rowVariableScope) {
      const inner = p.toStrategy(target, flat);
      const parentContext = parentContextFor(
        rowVariableScope.anchor,
        rowVariableScope.innerAncestors,
        {
          containsText: rowVariableScope.value,
          containsTextDynamic: {
            source: 'workflowState',
            key: rowVariableScope.key,
            originalText: rowVariableScope.value,
          },
        }
      );
      return {
        selectors: applyContexts(
          { ...inner, parentContext },
          frameContext,
          baseDynamicText
        ),
        predicate: `${p.name}+row-variable`,
        bindings: allBindings,
        irreducible: false,
      };
    }

    // Try scoping under each ancestor (closest-first). The scope must be
    // unique within the ancestor's subtree AND the ancestor itself must be
    // identifiable in the wider snapshot (so the chain resolves end-to-end
    // at replay). Prefer the ancestor's globally-unique stable id (data-id /
    // test-id) — a structural, NON-ordinal anchor that also identifies nameless
    // ancestors (a `__new__` draft row, a hard-coded test-id panel) — and emit
    // it as `parentContext.css`; otherwise identify the ancestor by role+name
    // / durable child text.
    for (let i = ancestors.length - 1; i >= 0; i--) {
      const anchor = ancestors[i];
      const sub = uniquenessMatchesScoped(flat, target, p, anchor);
      if (sub !== 1) continue;

      const idSelector = uniqueStableIdSelector(anchor, flat);
      if (idSelector) {
        return {
          selectors: applyContexts(
            { ...p.toStrategy(target, flat), parentContext: { css: idSelector } },
            frameContext,
            baseDynamicText
          ),
          predicate: `${p.name}+stable-id-ancestor`,
          bindings: allBindings,
          irreducible: false,
        };
      }

      if (!isAncestorIdentifiable(anchor, identityIndex, target)) continue;
      const inner = p.toStrategy(target, flat);
      const parentContext = parentContextFor(anchor, ancestors.slice(i + 1));
      return {
        selectors: applyContexts(
          { ...inner, parentContext },
          frameContext,
          baseDynamicText
        ),
        predicate: `${p.name}+ancestor`,
        bindings: allBindings,
        irreducible: false,
      };
    }
  }

  const rowScopedOrdinal = buildRowScopedOrdinalLocator({
    target,
    ancestors,
    variableValues,
  });
  if (rowScopedOrdinal) {
    return {
      selectors: applyContexts(
        {
          role: { role: target.el.role },
          playwrightLocator: rowScopedOrdinal.locator,
          playwrightLocatorCandidates: {
            deterministic: {
              locator: rowScopedOrdinal.locator,
              usesVariableBinding: true,
              boundVariableKey: rowScopedOrdinal.key,
            },
          },
          parentContext: parentContextFor(rowScopedOrdinal.anchor, rowScopedOrdinal.innerAncestors, {
            containsText: rowScopedOrdinal.value,
            containsTextDynamic: {
              source: 'workflowState',
              key: rowScopedOrdinal.key,
              originalText: rowScopedOrdinal.value,
            },
          }),
        },
        frameContext,
        baseDynamicText
      ),
      predicate: 'row-variable+scoped-button-index',
      bindings: allBindings,
      irreducible: false,
    };
  }

  // Clone-index disambiguation: when role+name matches N > 1 nodes and all
  // matches live in byte-identical subtrees under a common non-list parent,
  // they are UI clones (e.g. responsive-layout duplicates), not data rows.
  // Ordinal disambiguation is safe here because the matches carry no
  // information difference — picking nth(N) deterministically clicks the
  // same DOM element each replay. Distinguishes "Telerik dashboard widget
  // duplicated at 4 breakpoints" from "table with 4 rows of patient data."
  const cloneIndex = buildCloneIndexLocator({ flat, target });
  if (cloneIndex) {
    const role: RoleSelector = { role: target.el.role };
    const name = target.textTemplate ?? target.el.text;
    if (name) role.name = name;
    return {
      selectors: applyContexts(
        { role, playwrightLocator: cloneIndex.locator },
        frameContext,
        baseDynamicText
      ),
      predicate: 'role+name+clone-index',
      bindings: allBindings,
      irreducible: false,
    };
  }

  return {
    selectors: applyContexts(indexedFallback(target), frameContext, baseDynamicText),
    predicate: 'indexed-fallback',
    bindings: allBindings,
    irreducible: true,
  };
}

/**
 * Roles whose containers carry data-row semantics. When a shared ancestor of
 * the role+name matches has one of these roles, ordinal disambiguation is
 * brittle because list/grid contents are usually data-driven and reorder
 * with sorts, filters, or inserts. Keeps clone-index safe for static UI
 * duplication (dashboards, side-by-side panels) while ceding data lists
 * to indexed-fallback + irreducible.
 */
const LIST_ROLES: ReadonlySet<string> = new Set([
  'list',
  'listbox',
  'grid',
  'tree',
  'treegrid',
  'table',
  'tbody',
  'thead',
  'rowgroup',
  'row',
  'menu',
  'menubar',
]);

interface CloneIndexLocator {
  locator: string;
  ordinal: number;
}

/**
 * Build a `role+name+clone-index` locator when all role+name matches are
 * structurally identical clones under a non-list common parent. Returns
 * `null` if the safety conditions don't hold — caller falls through to
 * indexed-fallback + irreducible.
 *
 * Safety contract:
 *  1. ≥2 global matches for the target's (role, name).
 *  2. Each match has a "card" (lowest ancestor containing only that match),
 *     and all cards are siblings under a single common parent.
 *  3. No ancestor between the common parent and the document root has a
 *     list/grid/table-style role.
 *  4. All cards' subtree text fingerprints (recursive role+text walk) are
 *     byte-identical — proves the matches carry no distinguishing data.
 */
function buildCloneIndexLocator(args: {
  flat: NormalizedNode[];
  target: NormalizedNode;
}): CloneIndexLocator | null {
  const { flat, target } = args;
  if (!target.el.role) return null;
  const targetName = target.textTemplate ?? target.el.text;
  if (!targetName) return null;

  // Find all global matches for role+name in flatten/DOM order.
  const matches = flat.filter(
    (n) => n.el.role === target.el.role && (n.textTemplate ?? n.el.text) === targetName
  );
  if (matches.length < 2) return null;

  // Each match's "card" = lowest ancestor whose subtree contains ONLY that
  // match among `matches`. Walking up stops as soon as another match enters
  // the subtree.
  const cards: NormalizedNode[] = [];
  for (const m of matches) {
    const card = nearestCloneCard(m, matches);
    if (!card) return null;
    cards.push(card);
  }

  // All cards must share a single common parent (siblings of one another).
  const parents = new Set<NormalizedNode | null>(cards.map((c) => c.parent));
  if (parents.size !== 1) return null;
  const commonParent = [...parents][0];
  if (!commonParent) return null;

  // No list-role ancestor in the chain from the common parent up.
  for (let p: NormalizedNode | null = commonParent; p; p = p.parent) {
    if (LIST_ROLES.has(p.el.role)) return null;
  }

  // All cards must have byte-identical subtree text fingerprints.
  const reference = subtreeTextFingerprint(cards[0].el);
  for (let i = 1; i < cards.length; i++) {
    if (subtreeTextFingerprint(cards[i].el) !== reference) return null;
  }

  const ordinal = matches.indexOf(target);
  if (ordinal < 0) return null;

  const locator = `getByRole('${escapeLocatorString(target.el.role)}', { name: '${escapeLocatorString(targetName)}' }).nth(${ordinal})`;
  return { locator, ordinal };
}

function nearestCloneCard(
  match: NormalizedNode,
  allMatches: NormalizedNode[]
): NormalizedNode | null {
  const otherRefs = new Set(allMatches.filter((m) => m !== match).map((m) => m.el.ref));
  let candidate: NormalizedNode | null = match;
  let node: NormalizedNode | null = match.parent;
  while (node) {
    let containsOther = false;
    for (const ref of otherRefs) {
      if (node.descendantRefs.has(ref)) {
        containsOther = true;
        break;
      }
    }
    if (containsOther) break;
    candidate = node;
    node = node.parent;
  }
  return candidate;
}

/**
 * Recursive role+text walk of a subtree, ignoring refs and attributes.
 * Two clones of the same UI component produce identical fingerprints; two
 * data rows produce different fingerprints (their content varies).
 */
function subtreeTextFingerprint(el: SnapshotElement): string {
  // Capture every text-bearing slot the snapshot parser uses:
  //   `text`       — accessible name (button "Edit", link "Home", etc.)
  //   `inputValue` — generic-with-content (`- generic [ref=e1]: Hello`) and
  //                  textbox/combobox current value
  //   `url`        — link href when present
  // Attributes are intentionally excluded: clones can carry different ref-derived
  // attributes (test-id, data-id) without being information-distinct from a user's
  // perspective. Text/inputValue/url are the dimensions that actually distinguish
  // a "data row" from a "UI clone."
  const parts: string[] = [];
  const walk = (node: SnapshotElement): void => {
    const slots: string[] = [node.role];
    if (node.text) slots.push(`t=${node.text}`);
    if (node.inputValue) slots.push(`v=${node.inputValue}`);
    if (node.url) slots.push(`u=${node.url}`);
    parts.push(slots.join(':'));
    for (const c of node.children) walk(c);
  };
  walk(el);
  return parts.join('|');
}

interface RowScopedOrdinalLocator {
  locator: string;
  anchor: NormalizedNode;
  innerAncestors: NormalizedNode[];
  key: string;
  value: string;
}

function buildRowScopedOrdinalLocator(args: {
  target: NormalizedNode;
  ancestors: NormalizedNode[];
  variableValues: Record<string, string>;
}): RowScopedOrdinalLocator | null {
  const targetLabel =
    args.target.textTemplate ??
    args.target.el.text ??
    args.target.ariaLabelTemplate ??
    args.target.el.attributes['aria-label'];
  if (args.target.el.role !== 'button' || targetLabel) return null;

  const entries = Object.entries(args.variableValues)
    .filter(([, value]) => typeof value === 'string' && value.length >= MIN_TEMPLATING_LENGTH)
    .sort((a, b) => b[1].length - a[1].length);
  if (entries.length === 0) return null;

  for (let i = args.ancestors.length - 1; i >= 0; i--) {
    const anchor = args.ancestors[i];
    if (!REPEATING_ANCESTOR_ROLES.has(anchor.el.role)) continue;
    const anchorText = collectSubtreeText(anchor.el);
    const match = entries.find(([, value]) => textContainsValue(anchorText, value));
    if (!match) continue;
    const buttonRefs = collectRoleRefs(anchor.el, 'button');
    const ordinal = buttonRefs.indexOf(args.target.el.ref);
    if (ordinal < 0) continue;
    const [key, value] = match;
    return {
      locator: `getByRole('${escapeLocatorString(anchor.el.role)}').filter({ hasText: '{{var.${key}}}' }).getByRole('button').nth(${ordinal})`,
      anchor,
      innerAncestors: args.ancestors.slice(i + 1),
      key,
      value,
    };
  }

  return null;
}

function collectRoleRefs(el: SnapshotElement, role: string): string[] {
  const refs: string[] = [];
  const walk = (node: SnapshotElement): void => {
    if (node.role === role && node.ref) refs.push(node.ref);
    for (const child of node.children) walk(child);
  };
  walk(el);
  return refs;
}

function escapeLocatorString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

// ─────────────────────────────────────────────────────────────────────────────
// Templating
// ─────────────────────────────────────────────────────────────────────────────

/** Minimum length for a variable value to be templated. Short values (e.g. "1", "OK") are too lossy. */
const MIN_TEMPLATING_LENGTH = 3;

interface CompiledBinding {
  key: string;
  value: string;
  /** Word-boundary-aware regex, used for both check and replace. */
  regex: RegExp;
}

function templateAll(
  flat: NormalizedNode[],
  variableValues: Record<string, string>
): VariableBinding[] {
  const summary = new Map<string, VariableBinding>();
  const compiled: CompiledBinding[] = Object.entries(variableValues)
    .filter(([, v]) => typeof v === 'string' && v.length >= MIN_TEMPLATING_LENGTH)
    .map(([key, value]) => ({
      key,
      value,
      regex: new RegExp(`(^|\\W)${escapeRegex(value)}(?=\\W|$)`, 'g'),
    }));
  if (compiled.length === 0) return [];

  for (const node of flat) {
    templateField(node, 'text', node.el.text, (v) => {
      node.textTemplate = v;
    }, compiled, summary);
    templateField(
      node,
      'aria-label',
      node.el.attributes['aria-label'],
      (v) => {
        node.ariaLabelTemplate = v;
      },
      compiled,
      summary
    );
    templateField(
      node,
      'placeholder',
      node.el.attributes['placeholder'],
      (v) => {
        node.placeholderTemplate = v;
      },
      compiled,
      summary
    );
    templateField(
      node,
      'value',
      node.el.inputValue ?? node.el.attributes['value'],
      (v) => {
        node.valueTemplate = v;
      },
      compiled,
      summary
    );
  }

  return Array.from(summary.values());
}

function templateField(
  node: NormalizedNode,
  field: TemplatedField,
  original: string | undefined,
  set: (templated: string) => void,
  compiled: CompiledBinding[],
  summary: Map<string, VariableBinding>
): void {
  if (!original) return;
  let templated = original;
  const local: VariableBinding[] = [];
  for (const b of compiled) {
    b.regex.lastIndex = 0;
    let occurrences = 0;
    // Word-boundary-aware replace: the regex captures the boundary char on
    // the left so we preserve it in the replacement; the right boundary is
    // a lookahead so it isn't consumed.
    const next = templated.replace(b.regex, (_match, lead: string) => {
      occurrences++;
      return `${lead}{{${b.key}}}`;
    });
    if (occurrences > 0 && next !== templated) {
      templated = next;
      local.push({ key: b.key, value: b.value, field, occurrences });
    }
  }
  if (templated !== original) {
    set(templated);
    for (const b of local) {
      node.bindings.push(b);
      const k = `${b.key}|${b.field}`;
      const existing = summary.get(k);
      if (existing) existing.occurrences += b.occurrences;
      else summary.set(k, { ...b });
    }
  }
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ─────────────────────────────────────────────────────────────────────────────
// Predicate ladder
// ─────────────────────────────────────────────────────────────────────────────

interface Predicate {
  name: string;
  /** Substrate field this predicate reads — used to look up the durability source. */
  field: PredicateField;
  applies: (target: NormalizedNode) => boolean;
  /** Match function: returns true if `candidate` would be a match for the target's predicate values. */
  match: (target: NormalizedNode, candidate: NormalizedNode) => boolean;
  toStrategy: (target: NormalizedNode, flat: NormalizedNode[]) => SelectorStrategy;
}

const DOM_MUTATION_SOURCES: ReadonlySet<SnapshotElementSource> = new Set([
  'augmented:dom-mutation',
]);

/**
 * Look up the provenance for the field a predicate reads, and return true
 * when that source is too low-durability to use. Stable testIds are handled
 * before the predicate ladder.
 */
function isPredicateBlockedBySource(p: Predicate, target: NormalizedNode): boolean {
  const source =
    p.field === 'text'
      ? target.el.nameSource
      : target.el.attributeSources?.[p.field];
  if (!source) return false;
  return DOM_MUTATION_SOURCES.has(source);
}

/**
 * Walk the target's testIds and return the first one that's globally unique.
 * Each testId is independent, so set-overlap uniqueness (the regular predicate
 * ladder) doesn't fit — a target with `[shared, unique]` testIds shouldn't
 * fall through just because `shared` is duplicated.
 */
function pickUniqueTestId(target: NormalizedNode, flat: NormalizedNode[]): string | null {
  const ids = stableTestIds(target);
  for (const id of ids) {
    let count = 0;
    for (const n of flat) {
      if (n.el.cssHints?.testIds?.includes(id)) {
        count++;
        if (count > 1) break;
      }
    }
    if (count === 1) return id;
  }
  return null;
}

function stableTestIds(target: NormalizedNode): string[] {
  // Stability = survives a re-seed. Seeded ids (UUID/ULID/entity numbers) are
  // volatile even when unique in the recording snapshot. Canonical rule lives in
  // `@chatsdet/types` (`classifyIdentifier`).
  return (target.el.cssHints?.testIds ?? []).filter((id) => !isVolatileIdentifier(id));
}

function buildVolatileTestIdTextSelector(
  target: NormalizedNode,
  flat: NormalizedNode[]
): SelectorStrategy | null {
  const ids = target.el.cssHints?.testIds ?? [];
  const textParts = collectVolatileTestIdTextParts(target);
  if (textParts.length === 0) return null;

  for (const id of ids) {
    const prefix = stableIdentifierPrefix(id);
    const selectorBase = prefix
      ? `[data-testid^="${escapeCssString(prefix)}"]`
      : '[data-testid]';

    let count = 0;
    for (const n of flat) {
      const candidateIds = n.el.cssHints?.testIds ?? [];
      const matchingCandidate = prefix
        ? candidateIds.some((candidateId) => candidateId.startsWith(prefix))
        : candidateIds.some((candidateId) => isVolatileIdentifier(candidateId));
      if (!matchingCandidate) continue;
      const candidateTexts = collectTargetVisibleTexts(n);
      if (!textParts.every((part) => candidateTexts.includes(part))) continue;
      count++;
      if (count > 1) break;
    }
    if (count === 1) {
      const textFilters = textParts
        .map((part) =>
          prefix ? `:has-text("${escapeCssString(part)}")` : `:has(:text-is("${escapeCssString(part)}"))`
        )
        .join('');
      return {
        css: `${selectorBase}${textFilters}`,
        containsText: textParts,
      };
    }
  }

  return null;
}

function escapeCssString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * ARIA role → HTML tag(s) that imply that role natively. Used to build
 * CSS selectors that match both the implicit-role and explicit-role
 * forms (`<button class="foo">` AND `<div role="button" class="foo">`).
 *
 * **Why this matters:** the resolver skips a nameless role selector
 * when a CSS fallback is present (`element-resolver.ts:1487`), so the
 * CSS must work standalone. A naive `${role}.${cls}` selector emits
 * things like `generic.foo` (no `<generic>` tag exists in HTML) or
 * `link.foo` (`<link>` is for stylesheets, not clickables). The result
 * is a CSS that matches zero DOM nodes and replay fails.
 *
 * Roles without a stable implicit tag (`generic`, `switch`,
 * `menuitem`) get only the explicit `[role="..."]` form, which is
 * usually missing from the DOM (those roles are inferred). Callers
 * fall back to class-only selectors in that case (see roleScopedCss).
 */
const ROLE_TO_IMPLICIT_TAGS: Record<string, readonly string[]> = {
  button: ['button'],
  link: ['a'],
  img: ['img'],
  textbox: ['input', 'textarea'],
  combobox: ['select'],
  checkbox: ['input'],
  radio: ['input'],
};

/**
 * Build a CSS selector that matches an ARIA role's implicit-tag forms
 * AND its explicit `[role="..."]` form, joined by comma. The `suffix`
 * is appended to each branch (e.g. `.foo`, `[src*="x"]`, `:has(svg.y)`).
 *
 * For roles with no implicit-tag mapping (`generic`, etc.), returns
 * just the suffix — caller relies on substrate uniqueness because
 * tagging would emit a selector that matches nothing.
 */
function roleScopedCss(role: string, suffix: string): string {
  const implicit = ROLE_TO_IMPLICIT_TAGS[role] ?? [];
  if (implicit.length === 0) {
    // No reliable tag anchor; emit class-only / attr-only selector.
    // Trim leading `:` from `:has(...)` style suffixes — without a
    // leftmost subject `:has(...)` is invalid, so we fall back to `*`.
    return suffix.startsWith(':') ? `*${suffix}` : suffix;
  }
  const parts = [...implicit.map((t) => `${t}${suffix}`), `[role="${role}"]${suffix}`];
  return parts.join(', ');
}

/**
 * Return the target's icon class iff it's globally unique among same-role
 * nodes. Combined with the role tag, this produces a stable
 * `role:has(svg.<iconClass>)` selector for icon-only controls without an
 * accessible name.
 */
function pickUniqueIconClass(target: NormalizedNode, flat: NormalizedNode[]): string | null {
  const cls = target.el.iconClass;
  if (!cls) return null;
  let count = 0;
  for (const n of flat) {
    if (n.el.role === target.el.role && n.el.iconClass === cls) {
      count++;
      if (count > 1) return null;
    }
  }
  return count === 1 ? cls : null;
}

/**
 * Return the target's semantic class iff it's globally unique among same-role
 * nodes. Combined with the role tag, this produces a stable `role.<class>`
 * CSS selector for controls labeled by `extractSemanticHint(className)`.
 *
 * The class token has already passed the augmenter's noise filter (rejects
 * Tailwind utilities, styled-component hashes, CSS-in-JS hashes), so reaching
 * this helper means the substrate already carries a stable, semantic token.
 */
function pickUniqueSemanticClass(target: NormalizedNode, flat: NormalizedNode[]): string | null {
  const cls = target.el.semanticClass;
  if (!cls) return null;
  let count = 0;
  for (const n of flat) {
    if (n.el.role === target.el.role && n.el.semanticClass === cls) {
      count++;
      if (count > 1) return null;
    }
  }
  return count === 1 ? cls : null;
}

/**
 * Pick a stable filename-ish substring from a `src` URL for use in
 * `img[src*="<segment>"]` selectors. Strips query/hash and any path prefix;
 * keeps the last path segment. Returns null for empty / data-URLs / values
 * too short to disambiguate (`<8 chars`).
 */
function srcCssSegment(src: string | undefined): string | null {
  if (!src) return null;
  if (src.startsWith('data:')) return null;
  // Drop query + fragment.
  let cleaned = src.split('?')[0].split('#')[0];
  // Take last path segment.
  const slash = cleaned.lastIndexOf('/');
  if (slash >= 0) cleaned = cleaned.slice(slash + 1);
  cleaned = cleaned.trim();
  if (cleaned.length < 8) return null;
  return cleaned;
}

const rolePlusNamePredicate: Predicate = {
  name: 'role+name',
  field: 'text',
  applies: (t) => Boolean(t.el.role && (t.textTemplate ?? t.el.text)),
  match: (t, c) => {
    if (c.el.role !== t.el.role) return false;
    const tn = t.textTemplate ?? t.el.text;
    const cn = c.textTemplate ?? c.el.text;
    return Boolean(tn) && tn === cn;
  },
  toStrategy: (t) => {
    const role: RoleSelector = { role: t.el.role };
    const name = t.textTemplate ?? t.el.text;
    if (name) role.name = name;
    return { role };
  },
};

function placeholderValue(t: NormalizedNode): string | undefined {
  return inferredPlaceholderValue(t) ?? t.placeholderTemplate ?? t.el.attributes['placeholder'];
}

function inferredPlaceholderValue(t: NormalizedNode): string | undefined {
  // Playwright's aria snapshot can render placeholder-only inputs as
  // `textbox "Search": <value>`. That text is not a durable accessible name
  // for replay, so prefer a placeholder locator when the field is actively
  // value-bearing and the text looks like a search/filter placeholder.
  const text = t.textTemplate ?? t.el.text;
  if (
    t.el.role === 'textbox' &&
    text &&
    (t.valueTemplate ?? t.el.inputValue ?? t.el.attributes['value']) &&
    /^(search|filter|find)$/i.test(text.trim())
  ) {
    return text;
  }

  return undefined;
}

const ariaLabelPredicate: Predicate = {
  name: 'ariaLabel',
  field: 'aria-label',
  applies: (t) => Boolean(t.ariaLabelTemplate ?? t.el.attributes['aria-label']),
  match: (t, c) => {
    const tl = t.ariaLabelTemplate ?? t.el.attributes['aria-label'];
    const cl = c.ariaLabelTemplate ?? c.el.attributes['aria-label'];
    return Boolean(tl) && tl === cl;
  },
  toStrategy: (t) => ({
    ariaLabel: t.ariaLabelTemplate ?? t.el.attributes['aria-label'],
  }),
};

const placeholderPredicate: Predicate = {
  name: 'placeholder',
  field: 'placeholder',
  applies: (t) => Boolean(placeholderValue(t)),
  match: (t, c) => {
    const tp = placeholderValue(t);
    const cp = placeholderValue(c);
    return Boolean(tp) && tp === cp;
  },
  toStrategy: (t) => ({
    placeholder: placeholderValue(t),
  }),
};

const inferredPlaceholderPredicate: Predicate = {
  ...placeholderPredicate,
  applies: (t) => Boolean(inferredPlaceholderValue(t)),
  match: (t, c) => {
    const tp = inferredPlaceholderValue(t);
    const cp = inferredPlaceholderValue(c);
    return Boolean(tp) && tp === cp;
  },
};

/**
 * Anchor an `img` role on a substring of its `src` attribute. Last-resort
 * before role+containsText for unlabeled clickable images: the agent saw the
 * image's filename-derived label in render, but the substrate only carries
 * the raw `src`. We emit `img[src*="<filename>"]` so the locator still
 * grounds on a DOM-verifiable signal rather than tree-position.
 */
const imgSrcPredicate: Predicate = {
  name: 'imgSrc',
  field: 'src',
  applies: (t) => t.el.role === 'img' && Boolean(srcCssSegment(t.el.attributes['src'])),
  match: (t, c) => {
    if (c.el.role !== 'img') return false;
    const ts = srcCssSegment(t.el.attributes['src']);
    const cs = srcCssSegment(c.el.attributes['src']);
    return Boolean(ts) && ts === cs;
  },
  toStrategy: (t) => {
    const seg = srcCssSegment(t.el.attributes['src']) ?? '';
    return {
      role: { role: 'img' },
      css: roleScopedCss('img', `[src*="${seg}"]`),
    };
  },
};

/**
 * Anchor a grid cell on its `data-field` token (e.g. a MUI DataGrid editable
 * cell's `house_rate::GroupA`). A `data-field` is unique within a row but
 * repeats across rows, so this predicate is never globally unique — it relies
 * on the ladder's ancestor/row-variable scoping to bind it to a specific row.
 * That keeps it a stable, DOM-verifiable, NON-ordinal anchor: the right fix for
 * nameless editable cells the agent would otherwise only reach by coordinates.
 */
const dataFieldPredicate: Predicate = {
  name: 'dataField',
  field: 'data-field',
  applies: (t) => Boolean(t.el.dataField),
  match: (t, c) => Boolean(t.el.dataField) && c.el.dataField === t.el.dataField,
  // No bare `role` (a gridcell role is non-unique and the resolver tries role
  // before css). The `[data-field=…]` css IS the anchor; it resolves scoped
  // under the parentContext (row id / row-variable) the ladder attaches.
  toStrategy: (t) => ({
    css: roleScopedCss(t.el.role, `[data-field="${cssAttrEscape(t.el.dataField ?? '')}"]`),
  }),
};

function cssAttrEscape(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Stable id selectors for an element, from intentional entity-id attributes
 * only (`data-id`, `data-testid`). Generic `id` is deliberately excluded — it's
 * too often framework-generated (`mui-…`, React `:r3:`) to trust as an anchor.
 * Stability is decided by the canonical `stableAttrSelector` in `@chatsdet/types`
 * (a seeded entity number/id yields a `^prefix` or nothing, never an exact id).
 */
function elementStableIdSelectors(el: NormalizedNode['el']): string[] {
  const out: string[] = [];
  const dataId = el.cssHints?.dataId;
  const fromDataId = dataId ? stableAttrSelector('data-id', dataId) : null;
  if (fromDataId) out.push(fromDataId);
  for (const tid of el.cssHints?.testIds ?? []) {
    const s = stableAttrSelector('data-testid', tid);
    if (s) out.push(s);
  }
  return out;
}

/**
 * Count nodes that the stable-id `selector` would actually MATCH (cap at 2) —
 * by attribute semantics, not classified-string equality. A prefix selector
 * `[data-id^="__new__"]` matches `__new__template` too, even though that sibling
 * classifies to an exact selector; counting by `includes(selector)` would miss
 * it and wrongly mark the ancestor unique.
 */
function countNodesMatchingStableSelector(flat: NormalizedNode[], selector: string): number {
  const m = /^\[(data-id|data-testid)(\^?)="(.*)"\]$/.exec(selector);
  if (!m) return 0;
  const [, attr, op, escaped] = m;
  // Reverse `cssAttrEscape` (escape order was `\` then `"`).
  const value = escaped.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  const valuesOf = (el: NormalizedNode['el']): string[] =>
    attr === 'data-id'
      ? el.cssHints?.dataId
        ? [el.cssHints.dataId]
        : []
      : (el.cssHints?.testIds ?? []);

  let n = 0;
  for (const node of flat) {
    const vals = valuesOf(node.el);
    const hit =
      op === '^' ? vals.some((v) => v.startsWith(value)) : vals.some((v) => v === value);
    if (hit) {
      n++;
      if (n > 1) break;
    }
  }
  return n;
}

/**
 * The ancestor's globally-unique stable-id selector (`[data-id^="__new__"]`,
 * `[data-testid="settings-panel"]`, …), or null. This is how the per-predicate
 * `+ancestor` scope identifies a nameless ancestor by its durable entity id —
 * a structural, NON-ordinal anchor (`__new__` draft row, a hard-coded test-id
 * panel) — emitted as `parentContext.css` so the resolver scopes the target
 * (role or css) under it. Seeded ids reduce to a non-unique prefix and are
 * rejected here.
 */
function uniqueStableIdSelector(anchor: NormalizedNode, flat: NormalizedNode[]): string | null {
  for (const selector of elementStableIdSelectors(anchor.el)) {
    if (countNodesMatchingStableSelector(flat, selector) === 1) return selector;
  }
  return null;
}

const roleContainsTextPredicate: Predicate = {
  name: 'role+containsText',
  field: 'text',
  applies: (t) => {
    if (!t.el.role) return false;
    if (t.textTemplate ?? t.el.text) return false;
    return collectChildrenTexts(t.el).length > 0;
  },
  // Exact-set equality (sorted) — a candidate that's a strict superset of
  // the target's child texts must NOT match, otherwise uniqueness collapses.
  match: (t, c) => {
    if (c.el.role !== t.el.role) return false;
    const targetTexts = collectChildrenTexts(t.el).slice().sort();
    const candidateTexts = collectChildrenTexts(c.el).slice().sort();
    if (targetTexts.length !== candidateTexts.length) return false;
    return targetTexts.every((tx, i) => tx === candidateTexts[i]);
  },
  toStrategy: (t) => {
    const containsText = collectChildrenTexts(t.el).slice(0, 3);
    return {
      role: { role: t.el.role },
      containsText,
    };
  },
};

// testId is checked separately by `pickUniqueTestId` before this ladder runs.
// Same for iconClass and semanticClass (pre-ladder pickers).
const PREDICATES: ReadonlyArray<Predicate> = [
  inferredPlaceholderPredicate,
  rolePlusNamePredicate,
  ariaLabelPredicate,
  placeholderPredicate,
  imgSrcPredicate,
  dataFieldPredicate,
  roleContainsTextPredicate,
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

interface FlattenResult {
  flat: NormalizedNode[];
  byRef: Map<string, NormalizedNode>;
}

function flatten(roots: SnapshotElement[]): FlattenResult {
  const flat: NormalizedNode[] = [];
  const byRef = new Map<string, NormalizedNode>();
  function walk(el: SnapshotElement, parent: NormalizedNode | null): NormalizedNode {
    const node: NormalizedNode = {
      el,
      bindings: [],
      parent,
      descendantRefs: new Set(),
    };
    flat.push(node);
    if (el.ref) byRef.set(el.ref, node);
    for (const child of el.children) {
      const childNode = walk(child, node);
      // Bubble the child's descendant set up.
      node.descendantRefs.add(childNode.el.ref);
      for (const r of childNode.descendantRefs) node.descendantRefs.add(r);
    }
    return node;
  }
  for (const root of roots) walk(root, null);
  return { flat, byRef };
}

function ancestorChain(node: NormalizedNode): NormalizedNode[] {
  const chain: NormalizedNode[] = [];
  let cur = node.parent;
  while (cur) {
    chain.push(cur);
    cur = cur.parent;
  }
  // We want innermost-last (so closest-first iteration is i = length-1 → 0)
  // but the walk above produces innermost-first; reverse it for that contract.
  return chain.reverse();
}

/** Count predicate matches across the whole flat list, short-circuiting at 2. */
function uniquenessMatches(
  flat: NormalizedNode[],
  target: NormalizedNode,
  p: Predicate
): number {
  let count = 0;
  for (const n of flat) {
    if (p.match(target, n)) {
      count++;
      if (count > 1) return count;
    }
  }
  return count;
}

/**
 * Count predicate matches inside `anchor`'s subtree, short-circuiting at 2.
 * Uses the `descendantRefs` set computed during flatten so we don't re-walk.
 */
function uniquenessMatchesScoped(
  flat: NormalizedNode[],
  target: NormalizedNode,
  p: Predicate,
  anchor: NormalizedNode
): number {
  let count = 0;
  for (const n of flat) {
    if (n === anchor) continue;
    if (!anchor.descendantRefs.has(n.el.ref)) continue;
    if (p.match(target, n)) {
      count++;
      if (count > 1) return count;
    }
  }
  return count;
}

interface IdentityIndex {
  /** `${role}|${templatedName}` → count. */
  byRoleName: Map<string, number>;
  /** `${role}|${first durable child text}` → count for nameless containers. */
  byRoleChildText: Map<string, number>;
  /** templated aria-label → count. */
  byAriaLabel: Map<string, number>;
}

function buildIdentityIndex(flat: NormalizedNode[]): IdentityIndex {
  const byRoleName = new Map<string, number>();
  const byRoleChildText = new Map<string, number>();
  const byAriaLabel = new Map<string, number>();
  for (const n of flat) {
    const name = n.textTemplate ?? n.el.text;
    if (n.el.role && name) {
      const k = `${n.el.role}|${name}`;
      byRoleName.set(k, (byRoleName.get(k) ?? 0) + 1);
    }
    const childText = n.el.role && !name ? firstDurableAncestorChildText(n.el) : null;
    if (n.el.role && childText) {
      const k = `${n.el.role}|${childText}`;
      byRoleChildText.set(k, (byRoleChildText.get(k) ?? 0) + 1);
    }
    const ariaLabel = n.ariaLabelTemplate ?? n.el.attributes['aria-label'];
    if (ariaLabel) {
      byAriaLabel.set(ariaLabel, (byAriaLabel.get(ariaLabel) ?? 0) + 1);
    }
  }
  return { byRoleName, byRoleChildText, byAriaLabel };
}

/**
 * An ancestor is identifiable when its (role, templated name) pair is unique
 * across the snapshot, when it carries a testId, or when its aria-label is
 * unique. Without identifiability, the parentContext we build from it would
 * itself be ambiguous, defeating the disambiguation step.
 */
function isAncestorIdentifiable(
  anchor: NormalizedNode,
  idx: IdentityIndex,
  target: NormalizedNode
): boolean {
  // NOTE: stable id/test-id identification is handled by the caller via
  // `uniqueStableIdSelector` (which also enforces global uniqueness and emits
  // the id as `parentContext.css`). This function only covers role+name / child
  // text / aria-label identification.
  const role = anchor.el.role;
  if (role) {
    const name = anchor.textTemplate ?? anchor.el.text;
    if (name && idx.byRoleName.get(`${role}|${name}`) === 1) return true;
    const childText = !name ? firstDurableAncestorChildText(anchor.el) : null;
    const targetText = target.textTemplate ?? target.el.text;
    if (
      childText &&
      childText !== targetText &&
      idx.byRoleChildText.get(`${role}|${childText}`) === 1
    ) {
      return true;
    }
  }
  const ariaLabel = anchor.ariaLabelTemplate ?? anchor.el.attributes['aria-label'];
  if (ariaLabel && idx.byAriaLabel.get(ariaLabel) === 1) return true;
  return false;
}

function collectChildrenTexts(el: SnapshotElement): string[] {
  const texts: string[] = [];
  function walk(e: SnapshotElement) {
    if (e.text) texts.push(e.text);
    if (e.inputValue) texts.push(e.inputValue);
    for (const c of e.children) walk(c);
  }
  for (const c of el.children) walk(c);
  return texts;
}

function collectTargetVisibleTexts(target: NormalizedNode): string[] {
  const ownText = target.textTemplate ?? target.el.text;
  if (ownText) return [ownText];
  if (target.el.inputValue) return [target.el.inputValue];
  return collectChildrenTexts(target.el);
}

function firstDurableAncestorChildText(el: SnapshotElement): string | null {
  return collectChildrenTexts(el).find(isDurableAncestorChildText) ?? null;
}

function isDurableAncestorChildText(text: string): boolean {
  const trimmed = text.trim();
  // Keep this intentionally narrow; arbitrary row/card text is often data,
  // not a stable structural label for ancestor scoping.
  return /^(Section|Step)\s+\d+\s*:/i.test(trimmed);
}

function collectVolatileTestIdTextParts(target: NormalizedNode): string[] {
  const ownText = target.textTemplate ?? target.el.text;
  if (ownText) return [ownText];
  if (target.el.inputValue) return [target.el.inputValue];

  const childTexts = collectChildrenTexts(target.el);
  const durableChildTexts = childTexts.filter(isDurableVolatileTestIdChildText);
  return (durableChildTexts.length > 0 ? durableChildTexts : childTexts).slice(0, 3);
}

function isDurableVolatileTestIdChildText(text: string): boolean {
  return text.trim().length > 1;
}

interface RowVariableScope {
  anchor: NormalizedNode;
  innerAncestors: NormalizedNode[];
  key: string;
  value: string;
}

function findRowVariableScope(args: {
  flat: NormalizedNode[];
  target: NormalizedNode;
  predicate: Predicate;
  ancestors: NormalizedNode[];
  variableValues: Record<string, string>;
}): RowVariableScope | null {
  const entries = Object.entries(args.variableValues)
    .filter(([, value]) => typeof value === 'string' && value.length >= MIN_TEMPLATING_LENGTH)
    .sort((a, b) => b[1].length - a[1].length);
  if (entries.length === 0) return null;

  for (let i = args.ancestors.length - 1; i >= 0; i--) {
    const anchor = args.ancestors[i];
    if (!REPEATING_ANCESTOR_ROLES.has(anchor.el.role)) continue;
    if (uniquenessMatchesScoped(args.flat, args.target, args.predicate, anchor) !== 1) {
      continue;
    }

    const anchorText = collectSubtreeText(anchor.el);
    for (const [key, value] of entries) {
      if (!textContainsValue(anchorText, value)) continue;
      return {
        anchor,
        innerAncestors: args.ancestors.slice(i + 1),
        key,
        value,
      };
    }
  }

  return null;
}

function collectSubtreeText(el: SnapshotElement): string {
  const parts: string[] = [];
  function walk(node: SnapshotElement) {
    if (node.text) parts.push(node.text);
    if (node.inputValue) parts.push(node.inputValue);
    const ariaLabel = node.attributes['aria-label'];
    if (ariaLabel) parts.push(ariaLabel);
    for (const child of node.children) walk(child);
  }
  walk(el);
  return parts.join(' ');
}

function textContainsValue(text: string, value: string): boolean {
  const pattern = new RegExp(`(^|\\W)${escapeRegex(value)}(?=\\W|$)`);
  return pattern.test(text);
}

function frameContextFor(el: SnapshotElement): FrameContext | undefined {
  const sel = el.frameSelector;
  if (!sel) return undefined;
  return { type: 'iframe', selector: sel };
}

/**
 * Build a `parentContext` rooted at `anchor` and including any intermediate
 * ancestors between `anchor` and the target. The `parentChain` is innermost-
 * to-outermost per the {@link ParentContext} contract.
 */
function parentContextFor(
  anchor: NormalizedNode,
  inner: NormalizedNode[],
  override?: {
    containsText?: string;
    containsTextDynamic?: ParentContextDynamicText;
  }
): ParentContext {
  const ctx: ParentContext = { role: anchor.el.role };
  const anchorName = override?.containsText ? undefined : anchor.textTemplate ?? anchor.el.text;
  if (anchorName) ctx.name = anchorName;
  if (override?.containsText) {
    ctx.containsText = override.containsText;
    if (override.containsTextDynamic) {
      ctx.containsTextDynamic = override.containsTextDynamic;
    }
  } else if (!anchorName) {
    const childTexts = collectChildrenTexts(anchor.el);
    if (childTexts.length > 0) ctx.containsText = childTexts[0];
  }
  if (inner.length > 0) {
    // Innermost-to-outermost.
    // Row-variable scopes intentionally stop at the row. Resolving
    // `row.filter({ hasText }).locator(button)` is more reliable than adding
    // intermediate cells, which often duplicate the action button's name.
    if (!override?.containsText) {
      ctx.parentChain = inner
        .slice()
        .reverse()
        .map((n) => {
          const node: ParentContextNode = { role: n.el.role };
          const name = n.textTemplate ?? n.el.text;
          if (name) node.name = name;
          return node;
        });
    }
  }
  return ctx;
}

function indexedFallback(target: NormalizedNode): SelectorStrategy {
  // `siblingRoleIndex` is intentionally NOT mapped to `SelectorStrategy.itemIndex`
  // — that field is documented as a DOM `data-item-index` value and replay
  // will probe it as such. The selector-agent (or step-6 logging) handles
  // ambiguous fallbacks via the `irreducible` flag.
  const role: RoleSelector = { role: target.el.role };
  const name = target.textTemplate ?? target.el.text;
  if (name) role.name = name;
  return { role };
}

function applyContexts(
  base: SelectorStrategy,
  frame: FrameContext | undefined,
  dynamic: SelectorStrategy['dynamicText']
): SelectorStrategy {
  const out: SelectorStrategy = { ...base };
  if (frame) out.frameContext = frame;
  if (dynamic) out.dynamicText = dynamic;
  return out;
}

function buildDynamicText(target: NormalizedNode): SelectorStrategy['dynamicText'] {
  // Surface a dynamicText hint when the target's accessible name was rewritten.
  // When multiple bindings touched the text, pick the one with the longest
  // value (most-specific) so legacy replay paths get the strongest hint.
  const textBindings = target.bindings.filter((b) => b.field === 'text');
  if (textBindings.length === 0) return undefined;
  const original = target.el.text;
  if (!original) return undefined;
  const chosen = textBindings.slice().sort((a, b) => b.value.length - a.value.length)[0];
  return {
    source: 'workflowState',
    key: chosen.key,
    originalText: original,
  };
}
